import { SVGPathArray } from 'highcharts';
import { isNil } from 'lodash';
import type { SVGAttributes } from 'react';
import { DEFAULT_FONT_FAMILY } from './FontAttributesUtils';
import { getTextWidth } from './TextUtils';

const parser = new DOMParser();

// Detect CJK (Chinese, Japanese, Korean) characters
const CJK_CHARACTER_REGEX = /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uAC00-\uD7AF]/;

/**
 * Wrap CJK text character by character when no spaces are present.
 * It iterates through each character in the input text and adds it to the
 * current line until the line exceeds the maximum width. At that point, the
 * current line is added to the array of lines and a new line is started.
 *
 * @param {string} text - The text to be wrapped
 * @param {number} width - The maximum width the text can occupy
 * @param {string} fontAttributes - The font attributes of the text
 * @returns {string[]} - An array of strings representing the wrapped lines
 */
const wrapCJKText = (text: string, width: number, fontAttributes: string): string[] => {
  const lines: string[] = [];
  const characters = Array.from(text);
  let currentLine = '';

  for (const char of characters) {
    const testLine = currentLine + char;
    const testWidth = getTextWidth(testLine, fontAttributes);

    if (testWidth <= width) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = char;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

export const svgTextWrapWithLongText = (text: string, width: number, fontAttributes: string): string[] => {
  const lines: string[] = [];
  const unwrappedWidth = getTextWidth(text, fontAttributes);
  if (unwrappedWidth < width) {
    lines.push(text);
  } else {
    let charWidthMap = new Map();
    const textCharArr = text.split('');
    // Generate a map of char:width
    textCharArr.forEach((character: string) => {
      if (!charWidthMap.has(character)) {
        charWidthMap.set(character, getTextWidth(character, fontAttributes));
      }
    });

    let bufferTextWidth: number = 0;
    let buffer: string[] = [];
    /*
     * the idea is that we construct a buffer character by character
     * when the buffer width exceeds the max width, we save the buffer as new line
     * the saved buffer is cleared and the steps repeat
     */
    textCharArr.forEach((character: string, index: number) => {
      let wrapNextWord = false;
      if (character === ' ') {
        const indexOfNextWord = !textCharArr.includes(' ', index + 1)
          ? textCharArr.length
          : textCharArr.indexOf(' ', index + 1);
        const nextWord = textCharArr.slice(index, indexOfNextWord).join('');
        wrapNextWord = bufferTextWidth + getTextWidth(nextWord, fontAttributes) > width;
      }

      if (bufferTextWidth + charWidthMap.get(character) > width || wrapNextWord) {
        lines.push(buffer.join('').trim());
        // reset buffer, not the best way but shouldn't have much performance hit
        buffer = [];
        bufferTextWidth = 0;
      }
      buffer.push(character);
      bufferTextWidth += charWidthMap.get(character);
    });
    // if it has remaining buffer after going through last section of charArr
    if (buffer.length > 0) {
      lines.push(buffer.join('').trim());
    }
  }
  return lines;
};

// Given text and available width, return the text wrapped to the next line if required. Use sparingly due to performance impact in IE
export const svgTextWrap = (text: string, width: number, fontAttributes: string, minWords?: number): string[] => {
  const words = text.split(/\s+/);
  const hasCJKCharacters = CJK_CHARACTER_REGEX.test(text);
  const hasNoSpaces = words.length === 1;
  const hasParagraphBreak = text === '';

  // For CJK text or text without spaces (excluding paragraph breaks), use character-based wrapping
  if ((hasCJKCharacters || hasNoSpaces) && !hasParagraphBreak) {
    return wrapCJKText(text, width, fontAttributes);
  }

  let lines = [];
  let currentLine: string[] = [];
  let minWordsBeforeWrap = minWords || 0;

  const unwrappedWidth = getTextWidth(text, fontAttributes);
  // only do wrapping logic if needed
  if (unwrappedWidth < width) {
    lines = [text];
  } else {
    if (minWordsBeforeWrap === 0 && words.length > 3) {
      // skip words before attempting to wrap
      const estimatedWidthPerWord = unwrappedWidth / words.length;
      minWordsBeforeWrap = (width / estimatedWidthPerWord) * 0.66;
    }
    words.forEach((word) => {
      if (currentLine.length < minWordsBeforeWrap) {
        currentLine.push(word);
      } else {
        const newLine = [...currentLine, word];
        if (getTextWidth(newLine.join(' '), fontAttributes) > width) {
          if (currentLine.length) {
            lines.push(currentLine.join(' '));
          }
          currentLine = [word];
        } else {
          currentLine.push(word);
        }
      }
    });
    lines.push(currentLine.join(' '));
    if (lines[0] === '') {
      lines.shift();
    }
  }
  return lines;
};

// This svgTextWrapWithoutOverflow solve the issue overflow to next column if the last word is longer than leftover space.
export const svgTextWrapWithoutOverflow = (text: string, width: number, fontAttributes: string): string[] => {
  const words = text.split(' ');
  let lines = [];
  let currentLine = '';

  const unwrappedWidth = getTextWidth(text, fontAttributes);
  // only do wrapping logic if needed
  if (unwrappedWidth < width) {
    lines = [text];
  } else {
    for (const word of words) {
      // Calculate the width of the current line including the next word
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      if (getTextWidth(testLine, fontAttributes) <= width) {
        // If the current line fits within the maxWidth, add the word to it
        currentLine = testLine;
      } else {
        // If adding the word exceeds the maxWidth, push the current line to the array and start a new line
        lines.push(currentLine);
        currentLine = word;
      }
    }
  }
  // Push the last line to the array
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};
const ELLIPSIS = '...';

/*
 * Given text and available width, either return the full text or its truncated form with ellipsis. Use sparingly due to performance impact in IE
 * If forceEllipsis is true, ellipsis will be added even if text fits (useful when truncating multi-line text)
 */
export const svgTextEllipsis = (text: string, width: number, fontAttributes: string, forceEllipsis = false): string => {
  if (width <= 4) {
    return ELLIPSIS;
  }
  const elementWidth = getTextWidth(text, fontAttributes);

  // If forceEllipsis is true and text fits, append ellipsis and truncate if needed
  if (forceEllipsis && elementWidth <= width) {
    const textWithEllipsis = text + ELLIPSIS;
    if (getTextWidth(textWithEllipsis, fontAttributes) <= width) {
      return textWithEllipsis;
    }
    // Text + ellipsis doesn't fit, need to truncate
    let truncatedText = text;
    while (truncatedText.length > 0 && getTextWidth(truncatedText + ELLIPSIS, fontAttributes) > width) {
      truncatedText = truncatedText.slice(0, -1);
    }
    return truncatedText ? truncatedText + ELLIPSIS : ELLIPSIS;
  }

  // if the text exceeds the given width, truncate it until it fits
  if (elementWidth > width) {
    // recursive function to add/remove 1 letter from the text and re-test if the word fits in the width
    const adjustWidth = (text: string, prevLength: number, direction: number): string => {
      // adjustedText could be '' if our prevLength and direction are both negative and growing
      const adjustedText = text.slice(0, prevLength + direction);
      const elWidth = getTextWidth(adjustedText + ELLIPSIS, fontAttributes);
      if ((direction > 0 && elWidth < width) || (direction < 0 && elWidth > width)) {
        if (!adjustedText) {
          return ELLIPSIS;
        }
        return adjustWidth(text, prevLength + direction, direction);
      }
      return text.slice(0, prevLength) + ELLIPSIS;
    };
    const textLength = text.length;
    const diff = elementWidth - width;
    if (diff > 30) {
      // if the text is much bigger than the width, make an educated guess of what the truncated text length should be
      const pxPerChar = elementWidth / textLength;
      const estimatedLength = Math.floor(width / pxPerChar) - 2;
      const nextElementWidth = getTextWidth(text.slice(0, estimatedLength) + ELLIPSIS, fontAttributes);
      // then fine-tune the result by adding/removing characters
      return adjustWidth(text, estimatedLength, nextElementWidth > width ? -1 : 1);
    }
    // otherwise just remove characters one by one until it fits
    return adjustWidth(text, textLength, -1);
  }
  return text;
};

const nodeIsHtmlElement = (e: Node | Element): e is Element => {
  if (e?.hasOwnProperty('outerHTML')) {
    return !!(e as Element).outerHTML;
  }
  return false;
};

/*
 *  We MUST use DOMParser instead of a simple createElement and setting innerHTML because
 *  creating an actual element triggers opinionated behavior by the browser which "helpfully" does things
 *  like completely alter the image tags in the SVG.
 *
 *  In our case, since we are doing further manipulations on these elements before displaying,
 *  we want to delay that kind of behavior as late as possible in the life cycle.
 *
 *  Our templating system makes vital assumptions like "what is in the template is what you get"
 *
 */
export function createDOMFromSVGStr(svgStr: string): Document {
  // Explicitly fail on empty string
  if (isNil(svgStr)) {
    throw new Error('Cannot createDOMFromSVGStr on nil');
  }
  return parser.parseFromString(svgStr, 'image/svg+xml');
}

export function serializeFromXMLDocument(doc: Node | Element) {
  // Supported in some browsers such as Chrome
  if (nodeIsHtmlElement(doc)) {
    return doc.outerHTML;
  }
  // Otherwise, fall back XMLSerializer for IE
  const serializer = new XMLSerializer();
  return serializer.serializeToString(doc);
}

export const removeAllPlaceholderChildren = (element: SVGElement): void => {
  let child = element.firstChild;
  while (child) {
    element.removeChild(child);
    child = element.firstChild;
  }
};

export const getXandYValuesFromElementChild = (element: SVGTextElement): { x: number; y: number } => {
  let x = 0,
    y = 0;
  if (element) {
    const child = element.firstChild;
    const childElement = element.firstElementChild;
    /*
     * Check to see if there is any text in the first node. If there isn't
     * then get the x and y from the first element child.
     */
    if (child && childElement && (childElement === child || (child.textContent && !/[^\s]/.exec(child.textContent)))) {
      y = parseFloat(childElement.getAttribute('y') || '0');
      x = parseFloat(childElement.getAttribute('x') || '0');
    }
  }
  return { x, y };
};

/*
 * It is possible that font-size will not have a unit so we call parse float to get just the integer value
 * Then we add px to insure consistency
 */
export const getFontSize = (elem: SVGElement, fallback?: string): string => {
  const fontSizeAttr = elem.getAttribute('font-size');
  return fontSizeAttr ? parseFloat(fontSizeAttr) + 'px' : (fallback ?? '10px');
};

export const getFontFamily = (elem: SVGElement, fallback?: string): string => {
  const fontFamilyAttr = elem.getAttribute('font-family');
  return fontFamilyAttr ?? fallback ?? DEFAULT_FONT_FAMILY;
};

function transformCoordinates(x: number, y: number, width: number, height: number, ctm: DOMMatrix) {
  return {
    x: ctm.a * x + ctm.c * y + ctm.e,
    y: ctm.b * x + ctm.d * y + ctm.f,
    width: ctm.a * width,
    height: ctm.d * height,
  };
}

export type RectAttributes = { x: number; y: number; width: number; height: number; scale?: number };

const nodeIsSVGElement = (node: Node & { ownerSVGElement?: unknown }): node is SVGElement => !!node.ownerSVGElement;

const elementIsSVGSVGElement = (elem: SVGElement): elem is SVGSVGElement => elem.tagName === 'svg';

// Traverse up nested SVGs to root node so they return the correct nested transformations
function getRootTransformation(elem: Node | null, baseRectAttributes: RectAttributes): RectAttributes {
  if (elem?.parentNode && nodeIsSVGElement(elem)) {
    if (elementIsSVGSVGElement(elem) && elem.ownerSVGElement !== elem) {
      const { x, y, width, height } = baseRectAttributes;
      const ctm = typeof elem.getCTM === 'function' && elem.getCTM();
      if (!ctm) {
        return baseRectAttributes;
      }
      return getRootTransformation(elem.parentNode, transformCoordinates(x, y, width, height, ctm));
    }
    return getRootTransformation(elem.parentNode, baseRectAttributes);
  }
  return baseRectAttributes;
}

// This will get the absolute positional attributes for an SVG Rectangle taking parent transformations such as scale/translate into account
export const getAbsoluteRectAttributes = (elem: SVGRectElement): RectAttributes => {
  const x = parseFloat(elem.getAttribute('x') || '0');
  const y = parseFloat(elem.getAttribute('y') || '0');
  const width = parseFloat(elem.getAttribute('width') || '0');
  const height = parseFloat(elem.getAttribute('height') || '0');
  const ctm = typeof elem.getCTM === 'function' && elem.getCTM();
  if (!ctm) {
    return { x, y, width, height, scale: 1 };
  }
  const results = getRootTransformation(elem.parentNode, transformCoordinates(x, y, width, height, ctm));
  const scale = width ? results.width / width : 0;
  return { ...results, scale };
};

export const getSVGImageWidth = (svgStr: string, scale = 1): number => {
  const matches = /^<svg[^>]*width\s*=\s*"?(\d+)"?[^>]*>/.exec(svgStr);
  const match = matches?.[1];
  if (match) {
    return parseFloat(match) * scale;
  }
  return 0;
};
export const getSVGImageHeight = (svgStr: string, scale = 1): number => {
  const matches = /^<svg[^>]*height\s*=\s*"?(\d+)"?[^>]*>/.exec(svgStr);
  const match = matches?.[1];
  if (match) {
    return parseFloat(match) * scale;
  }
  return 0;
};

export const getSVGElementHeight = (svgElement: SVGSVGElement): number => svgElement.height.baseVal.value;

export function getSVGDimensions(svgStr: string, scale = 1): { width: number; height: number } {
  return {
    width: getSVGImageWidth(svgStr, scale),
    height: getSVGImageHeight(svgStr, scale),
  };
}

export const extractFontAttributesToString = (attributes: SVGAttributes<SVGTextContentElement>) => {
  const attributesList = [];
  // Font CSS-property strings must be in a very specific order
  const attributeOrder = ['fontWeight', 'fontSize', 'fontFamily'] as const;
  for (const attributeName of attributeOrder) {
    if (attributes[attributeName]) {
      attributesList.push(attributes[attributeName]);
    }
  }
  return attributesList.join(' ');
};

export const breakPath = (
  path: SVGPathArray,
  breakpoint: number,
  breakWidth: number = 10,
  deltaX: number = 10,
  deltaY: number = 10
): SVGPathArray => {
  const startX = path[0][1] as number;
  const startY = path[0][2] as number;
  const breakHalfWidth = Math.floor(breakWidth / 2);
  const breakLeftEdge = startX + breakpoint - breakHalfWidth;
  const breakRightEdge = startX + breakpoint + breakHalfWidth;
  const stop: SVGPathArray = [
    ['L', breakLeftEdge, startY],
    ['M', breakLeftEdge + deltaX, startY - deltaY],
  ];

  const leftSlanted: SVGPathArray = [
    ['L', breakLeftEdge - deltaX, startY + deltaY],
    ['M', breakRightEdge + deltaX, startY - deltaY],
  ];

  const rightSlanted: SVGPathArray = [
    ['L', breakRightEdge - deltaX, startY + deltaY],
    ['M', breakRightEdge, startY],
  ];

  return [...path.slice(0, 1), ...stop, ...leftSlanted, ...rightSlanted, ...path.slice(1)];
};
